import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ProjectFormData, Project } from '@/hooks/useProjectManagement';
import { AddressSearchOnly } from '@/components/Map/AddressSearchOnly';

interface ProjectFormProps {
  onSubmit: (data: ProjectFormData) => Promise<void>;
  initialData?: Project | null;
  isSubmitting: boolean;
  onCancel?: () => void;
}

export const ProjectForm: React.FC<ProjectFormProps> = ({
  onSubmit,
  initialData,
  isSubmitting,
  onCancel,
}) => {
  const [selectedLocation, setSelectedLocation] = useState<{
    address: string;
    lat: number;
    lng: number;
  } | null>(
    initialData?.address && initialData?.coordinates_lat && initialData?.coordinates_lng
      ? {
          address: initialData.address,
          lat: initialData.coordinates_lat,
          lng: initialData.coordinates_lng,
        }
      : null
  );

  const { register, handleSubmit, formState: { errors }, setValue, watch } = useForm<ProjectFormData>({
    defaultValues: initialData || {
      name: '',
      description: '',
      client_name: '',
      address: '',
      status: 'active',
      estimated_hours: undefined,
      start_date: undefined,
      end_date: undefined,
      coordinates_lat: undefined,
      coordinates_lng: undefined,
    },
  });

  const statusValue = watch('status');

  const handleLocationSelect = (location: { address: string; lat: number; lng: number }) => {
    setSelectedLocation(location);
    setValue('address', location.address);
    setValue('coordinates_lat', location.lat);
    setValue('coordinates_lng', location.lng);
  };

  const onFormSubmit = async (data: ProjectFormData) => {
    // Ajouter les coordonnées si elles ont été sélectionnées
    if (selectedLocation) {
      data.address = selectedLocation.address;
      data.coordinates_lat = selectedLocation.lat;
      data.coordinates_lng = selectedLocation.lng;
    }
    
    console.log('Données du formulaire à soumettre:', data);
    await onSubmit(data);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          {initialData ? 'Modifier le projet' : 'Créer un nouveau projet'}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit(onFormSubmit)} className="space-y-6">
          <Tabs defaultValue="basic" className="space-y-4">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="basic">Informations de base</TabsTrigger>
              <TabsTrigger value="location">Localisation</TabsTrigger>
            </TabsList>

            <TabsContent value="basic" className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Nom du projet *</Label>
                  <Input
                    id="name"
                    {...register('name', { required: 'Le nom du projet est requis' })}
                    placeholder="Nom du projet"
                  />
                  {errors.name && (
                    <p className="text-sm text-red-600">{errors.name.message}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="client_name">Client</Label>
                  <Input
                    id="client_name"
                    {...register('client_name')}
                    placeholder="Nom du client"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  {...register('description')}
                  placeholder="Description du projet"
                  rows={3}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="status">Statut</Label>
                  <Select
                    value={statusValue}
                    onValueChange={(value) => setValue('status', value as 'active' | 'completed' | 'on-hold')}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Sélectionner le statut" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Actif</SelectItem>
                      <SelectItem value="completed">Terminé</SelectItem>
                      <SelectItem value="on-hold">En pause</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="estimated_hours">Heures estimées</Label>
                  <Input
                    id="estimated_hours"
                    type="number"
                    {...register('estimated_hours', { 
                      valueAsNumber: true,
                      min: { value: 0, message: 'Les heures doivent être positives' }
                    })}
                    placeholder="Ex: 40"
                  />
                  {errors.estimated_hours && (
                    <p className="text-sm text-red-600">{errors.estimated_hours.message}</p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="start_date">Date de début</Label>
                  <Input
                    id="start_date"
                    type="date"
                    {...register('start_date')}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="end_date">Date de fin</Label>
                  <Input
                    id="end_date"
                    type="date"
                    {...register('end_date')}
                  />
                </div>
              </div>
            </TabsContent>

            <TabsContent value="location" className="space-y-4">
              <AddressSearchOnly
                onLocationSelect={handleLocationSelect}
                initialLocation={selectedLocation || undefined}
              />
            </TabsContent>
          </Tabs>

          <div className="flex gap-2 pt-4 border-t">
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Enregistrement...' : (initialData ? 'Mettre à jour' : 'Créer le projet')}
            </Button>
            {onCancel && (
              <Button type="button" variant="outline" onClick={onCancel}>
                Annuler
              </Button>
            )}
          </div>
        </form>
      </CardContent>
    </Card>
  );
};
